import React, { useEffect, useState } from 'react';

import { Botao, Field, Modal, Select } from '../../../ui';
import { OrigemFiltro, SituacaoEnvioFiltro } from '../../../api/carteira/carteiraApi';

export interface FiltrosCarteira {
  origem: OrigemFiltro;
  situacao: SituacaoEnvioFiltro;
}

export interface ModalFiltrosCarteiraProps {
  aberto: boolean;
  /** Filtros hoje aplicados na tela — viram o rascunho inicial do modal. */
  valor: FiltrosCarteira;
  onAplicar: (filtros: FiltrosCarteira) => void;
  onFechar: () => void;
}

export const FILTROS_CARTEIRA_VAZIOS: FiltrosCarteira = {
  origem: 'TODOS',
  situacao: 'TODOS',
};

export const contarFiltrosCarteiraAtivos = (f: FiltrosCarteira) =>
  (f.origem !== 'TODOS' ? 1 : 0) + (f.situacao !== 'TODOS' ? 1 : 0);

const OPCOES_SITUACAO = [
  { valor: 'TODOS', rotulo: 'Todos' },
  { valor: 'PENDENTE', rotulo: 'Pendente' },
  { valor: 'ENVIADO', rotulo: 'Enviado' },
] as const;

const OPCOES_ORIGEM = [
  { valor: 'TODOS', rotulo: 'Todas' },
  { valor: 'SAAC', rotulo: 'Cadastrado no SAAC' },
  { valor: 'SGA_IMPORTADO', rotulo: 'Importado do SGA' },
] as const;

/**
 * Filtros da carteira do consultor (at-32; reescrito na at-65, frente D).
 *
 * Trabalha sobre um **rascunho** e só devolve no "Aplicar" — trocar um select não
 * dispara requisição (padrão do `ModalFiltrosEnvios`, at-28). Os filtros ficam em
 * modal porque, soltos na barra, disputavam a linha com a busca e os contadores e
 * quebravam desalinhados no ambiente real.
 *
 * ## O que mudou na at-65
 *
 *   1. **Virou o `Modal` do kit.** O overlay era o `.filtros-overlay` do
 *      `ModalFiltrosEnvios.css`, com listener de `Escape` próprio e
 *      `aria-modal="true"` **sem prender o foco** — a promessa falsa ao leitor de
 *      tela que o `Modal` existe para encerrar. Ganhou de graça: foco que entra e
 *      volta para o botão "Filtros", Tab circulando dentro, e o título como `<h2>`.
 *   2. **Os campos viraram `Field` + `Select`.** O `<select>` cru herdava o
 *      `select { font-size: 16px }` do CSS legado e nada mais; agora tem a mesma
 *      moldura, a mesma seta e o mesmo foco dos campos do fluxo de adesão.
 *   3. **O rodapé alinha à DIREITA** (`alinharRodape`, nascido nesta branch): é a
 *      convenção de par de ações em diálogo, e era o caso que o `Modal` já previa
 *      em comentário desde a at-58.
 *
 * ⚠️ **O contrato exportado não mudou**: `FiltrosCarteira`,
 * `FILTROS_CARTEIRA_VAZIOS` e `contarFiltrosCarteiraAtivos` seguem daqui — a tela
 * os consome, e `'TODOS'` continua sendo o valor de "sem filtro" que a API espera
 * omitir.
 */
const ModalFiltrosCarteira: React.FC<ModalFiltrosCarteiraProps> = ({
  aberto,
  valor,
  onAplicar,
  onFechar,
}) => {
  const [rascunho, setRascunho] = useState<FiltrosCarteira>(valor);

  // Reabrir o modal tem de refletir o que está APLICADO, não o rascunho que o
  // consultor abandonou ao cancelar.
  useEffect(() => {
    if (aberto) setRascunho(valor);
  }, [aberto, valor]);

  const ativos = contarFiltrosCarteiraAtivos(rascunho);

  return (
    <Modal
      aberto={aberto}
      titulo="Filtros"
      descricao="Valem para a lista. Os totais do topo são da carteira inteira e não mudam."
      tamanho="sm"
      aoFechar={onFechar}
      alinharRodape="direita"
      rodape={
        <>
          {/* "Limpar" é o menos destrutivo dos três e fica à esquerda do par —
              desabilitado quando não há o que limpar, para o clique não ser um
              no-op silencioso. */}
          <Botao variante="fantasma" disabled={ativos === 0} onClick={() => setRascunho(FILTROS_CARTEIRA_VAZIOS)}>
            Limpar
          </Botao>
          <Botao variante="secundario" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao variante="primario" onClick={() => onAplicar(rascunho)}>
            Aplicar
          </Botao>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Field
          rotulo="Situação do envio"
          dica="Pendente = o associado ou algum veículo ainda por enviar ao SGA."
          campo="situacao"
        >
          <Select
            opcoes={OPCOES_SITUACAO.map((o) => ({ valor: o.valor, rotulo: o.rotulo }))}
            /* Sem placeholder: "Todos" JÁ é a opção de "sem filtro". Um
               "Selecione..." vazio faria o campo parecer não respondido. */
            placeholder=""
            value={rascunho.situacao}
            onChange={(e) =>
              setRascunho((r) => ({ ...r, situacao: e.target.value as SituacaoEnvioFiltro }))
            }
          />
        </Field>

        <Field
          rotulo="Origem do cadastro"
          dica="Casa o associado que tenha essa origem ou ao menos um veículo com ela."
          campo="origem"
        >
          <Select
            opcoes={OPCOES_ORIGEM.map((o) => ({ valor: o.valor, rotulo: o.rotulo }))}
            placeholder=""
            value={rascunho.origem}
            onChange={(e) => setRascunho((r) => ({ ...r, origem: e.target.value as OrigemFiltro }))}
          />
        </Field>
      </div>
    </Modal>
  );
};

export default ModalFiltrosCarteira;
